import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Pages
import LandingPage from "./pages/LandingPage";
import SplashPage from "./pages/SplashPage";
import AuthPage from "./pages/AuthPage";
import AgeVerificationPage from "./pages/AgeVerificationPage";
import FeedPage from "./pages/FeedPage";
import ExplorePage from "./pages/ExplorePage";
import BrowsePage from "./pages/BrowsePage";
import ProfilePage from "./pages/ProfilePage";
import CreatorProfilePage from "./pages/CreatorProfilePage";
import TokensPage from "./pages/TokensPage";
import SettingsPage from "./pages/SettingsPage";
import VerificationPage from "./pages/VerificationPage";
import AdminPage from "./pages/AdminPage";
import CreatorDashboardPage from "./pages/CreatorDashboardPage";
import LivePage from "./pages/LivePage";
import ReferralPage from "./pages/ReferralPage";
import LegalPage from "./pages/LegalPage";
import DealPage from "./pages/DealPage";
import SchedulePage from "./pages/SchedulePage";
import EarningsPage from "./pages/EarningsPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import GlobalStorePage from "./pages/GlobalStorePage";
import FeedbackPage from "./pages/FeedbackPage";
import BroadcastBanner from "./components/BroadcastBanner";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Protected Route
const ProtectedRoute = ({ children, requireAgeVerification = true }) => {
  const { isAuthenticated, isAgeVerified, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (requireAgeVerification && !isAgeVerified) {
    return <Navigate to="/verify-age" replace />;
  }

  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAgeVerified, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (!isAgeVerified) {
      return <Navigate to="/verify-age" replace />;
    }
    return <Navigate to="/feed" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><SplashPage /></PublicRoute>} />
      <Route path="/home" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      
      {/* Age Verification */}
      <Route path="/verify-age" element={
        <ProtectedRoute requireAgeVerification={false}>
          <AgeVerificationPage />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/browse" element={<BrowsePage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/:userId" element={<CreatorProfilePage />} />
        <Route path="/tokens" element={<TokensPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/verification" element={<VerificationPage />} />
        <Route path="/creator-dashboard" element={<CreatorDashboardPage />} />
        <Route path="/live" element={<LivePage />} />
        <Route path="/referrals" element={<ReferralPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/earnings" element={<EarningsPage />} />
        <Route path="/orders" element={<MyOrdersPage />} />
        <Route path="/store" element={<GlobalStorePage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Route>
      
      {/* Deal Pages (public, no auth required for viewing) */}
      <Route path="/deals/:dealCode" element={<DealPage />} />

      {/* Legal Pages (public, no auth required) */}
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/legal/:page" element={<LegalPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <BroadcastBanner />
          <AppRoutes />
          <Toaster position="top-center" richColors />
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
